import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, Loader2, Copy, Trash2, Pencil, Check, Undo2, ScrollText, ChevronUp, ChevronDown } from 'lucide-react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const ChatWidget = ({ isOpen: controlledIsOpen, onToggle: controlledOnToggle }) => {
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const isOpen = controlledIsOpen !== undefined ? controlledIsOpen : internalIsOpen;
  const onToggle = controlledOnToggle || (() => setInternalIsOpen((prev) => !prev));

  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: 'Welcome! At AI Empower, you can create your first chatbot for free. Click here to get started: [Create Chatbot](/wizard)',
    },
  ]);
  const [input, setInput] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editedText, setEditedText] = useState('');
  const [prevMessages, setPrevMessages] = useState(null);
  const [showUndo, setShowUndo] = useState(false);
  const [copiedCodeIndex, setCopiedCodeIndex] = useState(null);
  const [copiedAll, setCopiedAll] = useState(false);
  const [expandedMessages, setExpandedMessages] = useState({});
  const [showPopup, setShowPopup] = useState(false);
  const messagesEndRef = useRef(null);
  const navigate = useNavigate();
  const chatEndpoint = import.meta.env.VITE_XAI_CHAT_ENDPOINT;

  useEffect(() => {
    console.log('[ChatWidget] isOpen:', isOpen, 'controlledIsOpen:', controlledIsOpen, 'onToggle type:', typeof controlledOnToggle);
  }, [isOpen, controlledIsOpen, controlledOnToggle]);

  useEffect(() => {
    const hasSeenPopup = sessionStorage.getItem('hasSeenPopup');
    if (!hasSeenPopup && !isOpen) {
      console.log('[ChatWidget] Scheduling popup for 30s');
      const timer = setTimeout(() => {
        console.log('[ChatWidget] Showing popup');
        setShowPopup(true);
        sessionStorage.setItem('hasSeenPopup', 'true');
        const autoDismiss = setTimeout(() => {
          console.log('[ChatWidget] Auto-dismissing popup');
          setShowPopup(false);
        }, 7000);
        return () => clearTimeout(autoDismiss);
      }, 30000);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
    if (showUndo) {
      const timer = setTimeout(() => setShowUndo(false), 5000);
      return () => clearTimeout(timer);
    }
    if (copiedCodeIndex !== null) {
      const timer = setTimeout(() => setCopiedCodeIndex(null), 2000);
      return () => clearTimeout(timer);
    }
    if (copiedAll) {
      const timer = setTimeout(() => setCopiedAll(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [messages, isTyping, showUndo, copiedCodeIndex, copiedAll]);

  const sendMessage = async () => {
    if (!input.trim() || isSending) return;

    setIsSending(true);
    setIsTyping(true);
    const userMessage = { role: 'user', content: input };
    const currentMessages = [...messages];
    setMessages([...currentMessages, userMessage]);
    setInput('');

    const logPrefix = `[${new Date().toISOString()}] [ChatWidgetSend]`;
    console.log(`${logPrefix} Sending message:`, input);

    try {
      const response = await axios.post(chatEndpoint, {
        message: input,
        history: currentMessages,
      });
      const { reply, updatedHistory } = response.data;
      console.log(`${logPrefix} Raw API response:`, { reply, updatedHistory });
      const assistantMessage = { role: 'assistant', content: reply };
      setMessages(updatedHistory || [...currentMessages, userMessage, assistantMessage]);
    } catch (error) {
      console.error(`${logPrefix} Chat error:`, error);
      const errorMessage = { role: 'assistant', content: 'Sorry, something went wrong. Try again!' };
      setMessages([...currentMessages, userMessage, errorMessage]);
    } finally {
      setIsTyping(false);
      setIsSending(false);
    }
  };

  const sendEditedMessage = async (index) => {
    if (!editedText.trim() || isSending) return;

    setIsSending(true);
    setIsTyping(true);
    const editedMessage = { role: 'user', content: editedText };
    const newMessages = messages.slice(0, index + 1);
    newMessages[index] = editedMessage;
    setMessages(newMessages);
    setEditingIndex(null);

    const logPrefix = `[${new Date().toISOString()}] [ChatWidgetEditSend]`;
    console.log(`${logPrefix} Sending edited message:`, editedText);

    try {
      const response = await axios.post(chatEndpoint, {
        message: editedText,
        history: newMessages.slice(0, index),
      });
      const { reply, updatedHistory } = response.data;
      console.log(`${logPrefix} Raw API response:`, { reply, updatedHistory });
      const assistantMessage = { role: 'assistant', content: reply };
      setMessages(updatedHistory || [...newMessages, assistantMessage]);
    } catch (error) {
      console.error(`${logPrefix} Chat error:`, error);
      const errorMessage = { role: 'assistant', content: 'Sorry, something went wrong. Try again!' };
      setMessages([...newMessages, errorMessage]);
    } finally {
      setIsTyping(false);
      setIsSending(false);
    }
  };

  const clearChat = () => {
    setPrevMessages(messages);
    setMessages([]);
    setShowUndo(true);
  };

  const undoClear = () => {
    if (prevMessages) {
      setMessages(prevMessages);
      setPrevMessages(null);
      setShowUndo(false);
    }
  };

  const startEditing = (index, content) => {
    setEditingIndex(index);
    setEditedText(content);
  };

  const cancelEditing = () => {
    setEditingIndex(null);
    setEditedText('');
  };

  const toggleExpand = (index) => {
    setExpandedMessages((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const handleInputKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    } else if (e.key === 'Enter' && e.shiftKey) {
      e.preventDefault();
      setInput(input + '\n');
    }
  };

  const handleEditKeyDown = (e, index) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendEditedMessage(index);
    }
    if (e.key === 'Escape') cancelEditing();
  };

  const handleLinkClick = (path) => {
    navigate(path);
    onToggle();
  };

  const handleCopy = async (text, isCode = false, index = null) => {
    try {
      await navigator.clipboard.writeText(text);
      if (isCode) setCopiedCodeIndex(index);
      else setCopiedAll(true);
    } catch (error) {
      console.error('Copy failed:', error);
    }
  };

  const copyAllMessages = () => {
    const allText = messages.map((msg) => `${msg.role === 'user' ? 'You' : 'AI Empower'}: ${msg.content}`).join('\n');
    handleCopy(allText);
  };

  const renderMessageContent = (content, role, index) => {
    const isLong = content.length > 500;
    const isExpanded = expandedMessages[index];

    return (
      <>
        {isLong && !isExpanded ? (
          <div className="flex flex-col">
            <ReactMarkdown
              children={content.slice(0, 500) + '...'}
              remarkPlugins={[remarkGfm]}
              components={{
                a: ({ node, href, children, ...props }) => (
                  <span
                    className="text-blue-300 hover:text-blue-200 cursor-pointer underline transition-colors duration-300"
                    onClick={() => handleLinkClick(href)}
                    {...props}
                  >
                    {children}
                  </span>
                ),
                strong: ({ node, ...props }) => (
                  <span className={`font-bold relative ${role === 'user' ? 'text-blue-200' : 'text-purple-200'}`}>
                    {props.children}
                    <span
                      className={`absolute bottom-0 left-0 w-full h-0.5 bg-gradient-to-r ${
                        role === 'user' ? 'from-blue-700 to-blue-800' : 'from-purple-700 to-purple-800'
                      }`}
                    />
                  </span>
                ),
                em: ({ node, ...props }) => (
                  <span className={`italic transform -skew-x-12 ${role === 'user' ? 'text-blue-100' : 'text-purple-100'}`} {...props} />
                ),
                code: ({ node, inline, className, children, ...props }) => {
                  const codeIndex = inline ? null : index + '-' + Date.now();
                  const isCopied = copiedCodeIndex === codeIndex;
                  return inline ? (
                    <code
                      className={`p-0.5 rounded bg-opacity-80 text-xs font-mono text-white ${
                        role === 'user' ? 'bg-blue-950/80' : 'bg-purple-950/80'
                      }`}
                      {...props}
                    >
                      {children}
                    </code>
                  ) : (
                    <div className="relative group my-1">
                      <code
                        className={`block w-full p-1.5 rounded-lg bg-opacity-80 border text-xs font-mono text-white shadow-inner whitespace-pre-wrap overflow-x-auto scrollbar-custom ${
                          role === 'user' ? 'bg-blue-950/80 border-blue-800' : 'bg-purple-950/80 border-purple-800'
                        }`}
                        {...props}
                      >
                        {children}
                      </code>
                      <div className="relative group/copy">
                        <motion.button
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ duration: 0.2 }}
                          className={`absolute top-0.5 left-0.5 p-1 rounded-md bg-gradient-to-r ${
                            role === 'user' ? 'from-blue-500 to-blue-600' : 'from-purple-600 to-purple-700'
                          } text-white text-xs`}
                          onClick={() => handleCopy(children, true, codeIndex)}
                          aria-label="Copy code"
                        >
                          {isCopied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                        </motion.button>
                        <span
                          className="hidden md:block absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-blue-500 to-purple-600 text-white text-xs rounded py-1 px-2 opacity-0 group-hover/copy:opacity-100 transition-opacity duration-200 z-10"
                        >
                          {isCopied ? 'Copied!' : 'Copy'}
                        </span>
                      </div>
                    </div>
                  );
                },
                ul: ({ node, ...props }) => <ul className="my-1 pl-4" style={{ listStyleType: 'none' }} {...props} />,
                ol: ({ node, ...props }) => <ol className="my-1 pl-4" style={{ listStyleType: 'none' }} {...props} />,
                li: ({ node, ordered, ...props }) => {
                  const number = ordered ? props.index + 1 : null;
                  return (
                    <li className="flex items-baseline gap-2 my-1">
                      {ordered ? (
                        <span
                          className={`w-4 h-4 flex items-center justify-center text-xs font-bold rounded-full ${
                            role === 'user'
                              ? 'bg-gradient-to-r from-blue-500 to-blue-400 text-white'
                              : 'bg-gradient-to-r from-purple-600 to-purple-400 text-white'
                          }`}
                        >
                          {number}
                        </span>
                      ) : (
                        <span
                          className={`w-2 h-2 rounded-full mt-0.5 shadow-md ${
                            role === 'user' ? 'bg-blue-400 shadow-blue-500/50' : 'bg-purple-400 shadow-purple-500/50'
                          }`}
                        />
                      )}
                      <span className="flex-1">{props.children}</span>
                    </li>
                  );
                },
                table: ({ node, ...props }) => (
                  <table
                    className="my-2 border-collapse w-full bg-opacity-80"
                    style={{ background: role === 'user' ? 'rgba(30, 58, 138, 0.8)' : 'rgba(76, 29, 149, 0.8)' }}
                    {...props}
                  />
                ),
                th: ({ node, ...props }) => (
                  <th
                    className={`border p-2 text-left ${
                      role === 'user' ? 'border-blue-800 bg-blue-950/80' : 'border-purple-800 bg-purple-950/80'
                    }`}
                    {...props}
                  />
                ),
                td: ({ node, ...props }) => (
                  <td className={`border p-2 ${role === 'user' ? 'border-blue-800' : 'border-purple-800'}`} {...props} />
                ),
                p: ({ node, ...props }) => <p className="my-1 whitespace-pre-wrap" {...props} />,
              }}
            />
            <div className="relative group/expand">
              <motion.button
                onClick={() => toggleExpand(index)}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                className="mt-1 p-1 rounded-full bg-gradient-to-r from-purple-500 to-blue-500 text-white shadow-md hover:shadow-lg transition-all duration-300 self-end"
                aria-label="Expand message"
              >
                <motion.div
                  initial={{ rotate: 0 }}
                  animate={{ rotate: isExpanded ? 0 : 180 }}
                  transition={{ duration: 0.3, ease: 'easeInOut' }}
                >
                  <ChevronUp className="w-4 h-4" />
                </motion.div>
              </motion.button>
              <span
                className="hidden md:block absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-blue-500 to-purple-600 text-white text-xs rounded py-1 px-2 opacity-0 group-hover/expand:opacity-100 transition-opacity duration-200 z-10"
              >
                Expand
              </span>
            </div>
          </div>
        ) : (
          <>
            <ReactMarkdown
              children={content}
              remarkPlugins={[remarkGfm]}
              components={{
                a: ({ node, href, children, ...props }) => (
                  <span
                    className="text-blue-300 hover:text-blue-200 cursor-pointer underline transition-colors duration-300"
                    onClick={() => handleLinkClick(href)}
                    {...props}
                  >
                    {children}
                  </span>
                ),
                strong: ({ node, ...props }) => (
                  <span className={`font-bold relative ${role === 'user' ? 'text-blue-200' : 'text-purple-200'}`}>
                    {props.children}
                    <span
                      className={`absolute bottom-0 left-0 w-full h-0.5 bg-gradient-to-r ${
                        role === 'user' ? 'from-blue-700 to-blue-800' : 'from-purple-700 to-purple-800'
                      }`}
                    />
                  </span>
                ),
                em: ({ node, ...props }) => (
                  <span className={`italic transform -skew-x-12 ${role === 'user' ? 'text-blue-100' : 'text-purple-100'}`} {...props} />
                ),
                code: ({ node, inline, className, children, ...props }) => {
                  const codeIndex = inline ? null : index + '-' + Date.now();
                  const isCopied = copiedCodeIndex === codeIndex;
                  return inline ? (
                    <code
                      className={`p-0.5 rounded bg-opacity-80 text-xs font-mono text-white ${
                        role === 'user' ? 'bg-blue-950/80' : 'bg-purple-950/80'
                      }`}
                      {...props}
                    >
                      {children}
                    </code>
                  ) : (
                    <div className="relative group my-1">
                      <code
                        className={`block w-full p-1.5 rounded-lg bg-opacity-80 border text-xs font-mono text-white shadow-inner whitespace-pre-wrap overflow-x-auto scrollbar-custom ${
                          role === 'user' ? 'bg-blue-950/80 border-blue-800' : 'bg-purple-950/80 border-purple-800'
                        }`}
                        {...props}
                      >
                        {children}
                      </code>
                      <div className="relative group/copy">
                        <motion.button
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ duration: 0.2 }}
                          className={`absolute top-0.5 left-0.5 p-1 rounded-md bg-gradient-to-r ${
                            role === 'user' ? 'from-blue-500 to-blue-600' : 'from-purple-600 to-purple-700'
                          } text-white text-xs`}
                          onClick={() => handleCopy(children, true, codeIndex)}
                          aria-label="Copy code"
                        >
                          {isCopied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                        </motion.button>
                        <span
                          className="hidden md:block absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-blue-500 to-purple-600 text-white text-xs rounded py-1 px-2 opacity-0 group-hover/copy:opacity-100 transition-opacity duration-200 z-10"
                        >
                          {isCopied ? 'Copied!' : 'Copy'}
                        </span>
                      </div>
                    </div>
                  );
                },
                ul: ({ node, ...props }) => <ul className="my-1 pl-4" style={{ listStyleType: 'none' }} {...props} />,
                ol: ({ node, ...props }) => <ol className="my-1 pl-4" style={{ listStyleType: 'none' }} {...props} />,
                li: ({ node, ordered, ...props }) => {
                  const number = ordered ? props.index + 1 : null;
                  return (
                    <li className="flex items-baseline gap-2 my-1">
                      {ordered ? (
                        <span
                          className={`w-4 h-4 flex items-center justify-center text-xs font-bold rounded-full ${
                            role === 'user'
                              ? 'bg-gradient-to-r from-blue-500 to-blue-400 text-white'
                              : 'bg-gradient-to-r from-purple-600 to-purple-400 text-white'
                          }`}
                        >
                          {number}
                        </span>
                      ) : (
                        <span
                          className={`w-2 h-2 rounded-full mt-0.5 shadow-md ${
                            role === 'user' ? 'bg-blue-400 shadow-blue-500/50' : 'bg-purple-400 shadow-purple-500/50'
                          }`}
                        />
                      )}
                      <span className="flex-1">{props.children}</span>
                    </li>
                  );
                },
                table: ({ node, ...props }) => (
                  <table
                    className="my-2 border-collapse w-full bg-opacity-80"
                    style={{ background: role === 'user' ? 'rgba(30, 58, 138, 0.8)' : 'rgba(76, 29, 149, 0.8)' }}
                    {...props}
                  />
                ),
                th: ({ node, ...props }) => (
                  <th
                    className={`border p-2 text-left ${
                      role === 'user' ? 'border-blue-800 bg-blue-950/80' : 'border-purple-800 bg-purple-950/80'
                    }`}
                    {...props}
                  />
                ),
                td: ({ node, ...props }) => (
                  <td className={`border p-2 ${role === 'user' ? 'border-blue-800' : 'border-purple-800'}`} {...props} />
                ),
                p: ({ node, ...props }) => <p className="my-1 whitespace-pre-wrap" {...props} />,
              }}
            />
            {isLong && (
              <div className="relative group/collapse">
                <motion.button
                  onClick={() => toggleExpand(index)}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  className="mt-1 p-1 rounded-full bg-gradient-to-r from-purple-500 to-blue-500 text-white shadow-md hover:shadow-lg transition-all duration-300 self-end"
                  aria-label="Collapse message"
                >
                  <motion.div
                    initial={{ rotate: 0 }}
                    animate={{ rotate: isExpanded ? 0 : 180 }}
                    transition={{ duration: 0.3, ease: 'easeInOut' }}
                  >
                    <ChevronDown className="w-4 h-4" />
                  </motion.div>
                </motion.button>
                <span
                  className="hidden md:block absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-blue-500 to-purple-600 text-white text-xs rounded py-1 px-2 opacity-0 group-hover/collapse:opacity-100 transition-opacity duration-200 z-10"
                >
                  Collapse
                </span>
              </div>
            )}
          </>
        )}
        {role === 'user' && editingIndex !== index && (
          <motion.div
            className="absolute -bottom-6 right-1 opacity-0 group-hover:opacity-100 md:opacity-100"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 300 }}
          >
            <div className="relative group/edit">
              <button
                onClick={() => startEditing(index, content)}
                className="p-1 rounded-full bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-md hover:shadow-lg transition-all duration-300"
                aria-label="Edit message"
              >
                <Pencil className="w-5 h-5" />
              </button>
              <span
                className="hidden md:block absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-blue-500 to-purple-600 text-white text-xs rounded py-1 px-2 opacity-0 group-hover/edit:opacity-100 transition-opacity duration-200 z-10"
              >
                Edit
              </span>
            </div>
          </motion.div>
        )}
      </>
    );
  };

  const iconVariants = {
    idle: { scale: 1 },
    hover: { scale: 1.1 },
    tap: { scale: 0.95 },
  };

  const windowVariants = {
    hidden: { opacity: 0, x: '100%' },
    visible: { opacity: 1, x: 0, transition: { duration: 0.4, ease: 'easeOut' } },
    exit: { opacity: 0, x: '100%', transition: { duration: 0.4, ease: 'easeIn' } },
  };

  const popupVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: 'easeOut' } },
    exit: { opacity: 0, y: 20, transition: { duration: 0.3, ease: 'easeIn' } },
  };

  return (
    <>
      {/* Sticky Chat Icon */}
      <motion.div
        className="fixed bottom-6 right-6 z-50"
        variants={iconVariants}
        initial="idle"
        whileHover="hover"
        whileTap="tap"
        transition={{ type: 'spring', stiffness: 300, damping: 20 }}
      >
        <button
          onClick={() => {
            console.log('[ChatWidget] Sticky icon clicked, calling onToggle, current isOpen:', isOpen);
            onToggle();
          }}
          className="relative w-12 h-12 rounded-full bg-gradient-to-br from-blue-600 to-purple-600 shadow-lg hover:shadow-xl focus:outline-none transition-all duration-300 overflow-hidden border border-blue-400/60"
          aria-label="Toggle chat"
        >
          <img
            src="https://i.imgur.com/s42ZPoP.gif"
            alt="Chat Icon"
            className="w-full h-full object-cover brightness-125 contrast-150"
          />
        </button>
      </motion.div>

      {/* Popup Notification */}
      <AnimatePresence>
        {showPopup && (
          <motion.div
            variants={popupVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="fixed bottom-20 right-6 w-64 sm:w-80 max-w-[80%] bg-gradient-to-br from-gray-900/95 to-purple-900/95 rounded-2xl shadow-xl z-40 border border-purple-600/60 backdrop-blur-md p-4 flex flex-col gap-3"
          >
            <div className="flex items-center gap-3">
              <motion.img
                src="https://i.imgur.com/2OPgGx8.gif"
                alt="Bot Avatar"
                className="w-8 h-8 rounded-full border border-purple-500/60 brightness-110"
                whileHover={{ scale: 1.1 }}
                transition={{ type: 'spring', stiffness: 300 }}
              />
              <p className="text-white text-sm font-semibold">
                Hey, I’m your AI assistant! Ready to answer your questions?
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <motion.button
                onClick={() => {
                  console.log('[ChatWidget] Chat now clicked, calling onToggle, current isOpen:', isOpen);
                  setShowPopup(false);
                  onToggle();
                }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-3 py-1 bg-gradient-to-r from-blue-600 to-purple-600 text-white text-xs font-semibold rounded-full shadow-md hover:shadow-lg transition-all duration-300 min-w-[60px] min-h-[44px] flex items-center justify-center"
                aria-label="Chat now"
              >
                Chat now
              </motion.button>
              <motion.button
                onClick={() => {
                  console.log('[ChatWidget] No thanks clicked, hiding popup');
                  setShowPopup(false);
                }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-3 py-1 bg-gray-700/70 text-white text-xs font-semibold rounded-full shadow-md hover:shadow-lg transition-all duration-300 min-w-[60px] min-h-[44px] flex items-center justify-center"
                aria-label="Dismiss"
              >
                No thanks
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            variants={windowVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="fixed bottom-20 right-6 w-80 md:w-96 h-[70vh] max-h-[600px] bg-gradient-to-br from-gray-900/95 to-purple-900/95 rounded-3xl shadow-xl overflow-hidden z-50 border border-gradient-to-r from-purple-600 to-blue-600 backdrop-blur-md flex flex-col"
          >
            <motion.div className="flex justify-between items-center p-2 shadow-lg bg-gradient-to-r from-blue-600 to-purple-600 backdrop-blur-md">
              <div className="flex items-center gap-2">
                <motion.img
                  src="https://i.imgur.com/2OPgGx8.gif"
                  alt="Bot Avatar"
                  className="w-6 h-6 rounded-full border border-purple-500/60 brightness-110"
                  whileHover={{ scale: 1.1 }}
                  transition={{ type: 'spring', stiffness: 300 }}
                />
                <h3 className="text-white font-bold text-sm tracking-wide drop-shadow-md">AI Assistant</h3>
              </div>
              <div className="flex items-center gap-2">
                <div className="relative group">
                  <motion.button
                    onClick={() => handleLinkClick('/wizard')}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                    className="bg-white/10 backdrop-blur-md text-white text-xs font-semibold rounded-lg hover:bg-white/20 transition-all duration-300 py-1 px-2"
                    aria-label="Create Chatbot"
                  >
                    Create Chatbot
                  </motion.button>
                  <span className="hidden md:block absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-blue-500 to-purple-600 text-white text-xs rounded py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10">
                    Create Chatbot
                  </span>
                </div>
                <div className="relative group">
                  <motion.button
                    onClick={clearChat}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                    className="text-white focus:outline-none transition-all duration-300 p-1"
                    aria-label="Clear chat"
                  >
                    <Trash2 className="w-6 h-6" />
                  </motion.button>
                  <span className="hidden md:block absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-blue-500 to-purple-600 text-white text-xs rounded py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10">
                    Clear Chat
                  </span>
                </div>
                <div className="relative group">
                  <motion.button
                    onClick={copyAllMessages}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                    className="text-white focus:outline-none transition-all duration-300 p-1"
                    aria-label="Copy all messages"
                  >
                    {copiedAll ? <Check className="w-6 h-6" /> : <ScrollText className="w-6 h-6" />}
                  </motion.button>
                  <span className="hidden md:block absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-blue-500 to-purple-600 text-white text-xs rounded py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10">
                    {copiedAll ? 'Copied!' : 'Copy All'}
                  </span>
                </div>
                <div className="relative group">
                  <motion.button
                    onClick={() => {
                      console.log('[ChatWidget] Close button clicked, calling onToggle, current isOpen:', isOpen);
                      onToggle();
                    }}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                    className="text-red-400 hover:text-red-300 focus:outline-none transition-all duration-300 p-1"
                    aria-label="Close chat"
                  >
                    <X className="w-6 h-6" />
                  </motion.button>
                  <span className="hidden md:block absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-blue-500 to-purple-600 text-white text-xs rounded py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10">
                    Close
                  </span>
                </div>
              </div>
            </motion.div>

            <div className="relative flex-1 p-2 overflow-y-auto scrollbar-custom">
              <style>
                {`
                  .scrollbar-custom::-webkit-scrollbar {
                    width: 4px;
                  }
                  .scrollbar-custom::-webkit-scrollbar-track {
                    background: transparent;
                  }
                  .scrollbar-custom::-webkit-scrollbar-thumb {
                    background: linear-gradient(to bottom, #8B5CF6, #3B82F6);
                    border-radius: 2px;
                  }
                  .scrollbar-custom::-webkit-scrollbar-thumb:hover {
                    background: linear-gradient(to bottom, #A78BFA, #60A5FA);
                  }
                `}
              </style>
              <div
                className="absolute inset-0 opacity-10 pointer-events-none"
                style={{
                  backgroundImage: `url('https://i.imgur.com/8uB1VZC.gif')`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }}
              />
              <div className="relative z-10">
                <AnimatePresence>
                  {showUndo && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.3 }}
                      className="flex justify-center mb-2"
                    >
                      <motion.button
                        onClick={undoClear}
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.95 }}
                        className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-2 py-0.5 rounded-lg text-xs font-semibold shadow-md hover:shadow-lg transition-all duration-300"
                      >
                        <Undo2 className="w-3 h-3 inline mr-1" /> Undo
                      </motion.button>
                    </motion.div>
                  )}
                </AnimatePresence>
                {messages.map((msg, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, ease: 'easeOut' }}
                    className={`mb-2 ${msg.role === 'user' ? 'flex justify-end' : 'flex items-start gap-2'}`}
                  >
                    {msg.role === 'assistant' && (
                      <motion.img
                        src="https://i.imgur.com/2OPgGx8.gif"
                        alt="Bot Avatar"
                        className="w-6 h-6 rounded-full border border-purple-500/50 brightness-110"
                        whileHover={{ scale: 1.1 }}
                        transition={{ type: 'spring', stiffness: 300 }}
                      />
                    )}
                    <motion.div
                      className={`relative group inline-block rounded-xl shadow-md max-w-[70%] ${
                        msg.role === 'user'
                          ? `bg-gradient-to-r from-blue-500 to-blue-600 text-white ${msg.content.includes('\n') ? 'p-2' : 'py-1 px-2'}`
                          : 'bg-gradient-to-r from-purple-600 to-purple-700 text-white p-2'
                      } border border-blue-700/30 transition-all duration-300`}
                      whileHover={{ scale: 1.02, boxShadow: '0 0 10px rgba(139, 92, 246, 0.5)' }}
                    >
                      {editingIndex === index && msg.role === 'user' ? (
                        <div className="flex items-center gap-2">
                          <textarea
                            value={editedText}
                            onChange={(e) => setEditedText(e.target.value)}
                            onKeyDown={(e) => handleEditKeyDown(e, index)}
                            className="flex-1 p-2 rounded-lg bg-gray-900/90 text-white border border-gradient-to-r from-blue-500 to-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-500 transition-all duration-300 text-sm resize-none"
                            rows={Math.min(editedText.split('\n').length, 5)}
                            autoFocus
                          />
                          <div className="relative group/save">
                            <motion.button
                              onClick={() => sendEditedMessage(index)}
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.95 }}
                              className="p-1 rounded-full bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-md hover:shadow-lg"
                              aria-label="Save edit"
                            >
                              <Check className="w-5 h-5" />
                            </motion.button>
                            <span
                              className="hidden md:block absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-blue-500 to-purple-600 text-white text-xs rounded py-1 px-2 opacity-0 group-hover/save:opacity-100 transition-opacity duration-200 z-10"
                            >
                              Save
                            </span>
                          </div>
                          <div className="relative group/cancel">
                            <motion.button
                              onClick={cancelEditing}
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.95 }}
                              className="p-1 rounded-full bg-gradient-to-r from-red-500 to-red-600 text-white shadow-md hover:shadow-lg"
                              aria-label="Cancel edit"
                            >
                              <X className="w-5 h-5" />
                            </motion.button>
                            <span
                              className="hidden md:block absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-blue-500 to-purple-600 text-white text-xs rounded py-1 px-2 opacity-0 group-hover/cancel:opacity-100 transition-opacity duration-200 z-10"
                            >
                              Cancel
                            </span>
                          </div>
                        </div>
                      ) : (
                        renderMessageContent(msg.content, msg.role, index)
                      )}
                    </motion.div>
                  </motion.div>
                ))}
                <AnimatePresence>
                  {isTyping && (
                    <motion.div
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -10 }}
                      transition={{ duration: 0.3 }}
                      className="flex items-start gap-2 mb-2"
                    >
                      <img
                        src="https://i.imgur.com/2OPgGx8.gif"
                        alt="Bot Avatar"
                        className="w-6 h-6 rounded-full border border-purple-500/50 brightness-110"
                      />
                      <div className="flex gap-1">
                        <motion.div
                          className="w-1.5 h-1.5 bg-gradient-to-r from-purple-600 to-blue-600 rounded-full"
                          animate={{ y: [0, -3, 0] }}
                          transition={{ duration: 0.6, repeat: Infinity, delay: 0 }}
                        />
                        <motion.div
                          className="w-1.5 h-1.5 bg-gradient-to-r from-purple-600 to-blue-600 rounded-full"
                          animate={{ y: [0, -3, 0] }}
                          transition={{ duration: 0.6, repeat: Infinity, delay: 0.2 }}
                        />
                        <motion.div
                          className="w-1.5 h-1.5 bg-gradient-to-r from-purple-600 to-blue-600 rounded-full"
                          animate={{ y: [0, -3, 0] }}
                          transition={{ duration: 0.6, repeat: Infinity, delay: 0.4 }}
                        />
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
                <div ref={messagesEndRef} />
              </div>
            </div>

            <div className="p-2 bg-gradient-to-r from-blue-800/70 to-purple-800/70 border-t border-blue-800/60">
              <div className="flex items-center gap-2">
                <motion.textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleInputKeyDown}
                  placeholder="Ask in any language"
                  className={`flex-1 p-2 text-white border border-gradient-to-r from-purple-600 to-blue-600 focus:outline-none focus:ring-2 focus:ring-purple-600 transition-all duration-300 ease-in-out placeholder-gray-400 text-sm shadow-inner backdrop-blur-sm resize-none bg-gray-900/90 ${
                    input.split('\n').length > 2 ? 'rounded-lg' : 'rounded-full'
                  }`}
                  rows={Math.min(input.split('\n').length, 5)}
                  whileFocus={{ scale: 1.02, boxShadow: '0 0 10px rgba(139, 92, 246, 0.6)' }}
                />
                <motion.button
                  onClick={sendMessage}
                  disabled={isSending || !input.trim()}
                  whileHover={{ scale: 1.1, boxShadow: '0 0 10px rgba(59, 130, 246, 0.6)' }}
                  whileTap={{ scale: 0.95 }}
                  className={`bg-gradient-to-r from-blue-600 to-purple-600 p-2 rounded-full text-white shadow-md hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-300 ${
                    isSending || !input.trim() ? 'opacity-60 cursor-not-allowed' : ''
                  }`}
                  aria-label="Send message"
                >
                  {isSending ? <Loader2 className="w-6 h-6 animate-spin" /> : <Send className="w-6 h-6" />}
                </motion.button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ChatWidget;